/**
 * Regra de exibição do card "A repassar aos técnicos" (Dashboard).
 *
 * Vive fora do componente porque é a parte que erra: foi aqui que o card
 * foi ao ar mostrando valores cuja soma não fechava com o total do rodapé.
 * Como função pura, dá para travar a invariante em teste.
 */

export interface PayoutRow {
  technicianId: number;
  technicianName: string;
  /** Cliente JÁ pagou a fatura → dinheiro em caixa, pode repassar hoje. */
  releasedTotal: number;
  /** Tudo que está pendente (liberado + ainda preso ao cliente). */
  pendingTotal: number;
}

export interface RankedPayouts<T extends PayoutRow> {
  /** Os `top` maiores, já ordenados. */
  rows: T[];
  /** Quantos ficaram de fora do corte. */
  othersCount: number;
  /** Soma dos que ficaram de fora — para o "+N outros · X €". */
  othersTotal: number;
  /** Soma de TUDO que está sendo representado (rows + others). */
  shownTotal: number;
  /** A métrica usada, para o card formatar cada linha com o mesmo critério. */
  metricOf: (r: PayoutRow) => number;
}

/**
 * Ordena e corta a lista de técnicos a pagar.
 *
 * A métrica acompanha o estado do card — e é isso que mantém a conta
 * fechando para quem soma na mão:
 *   `urgent`  → mostra o LIBERADO (o mesmo número que o rodapé destaca);
 *   caso não  → mostra o PENDENTE, já que não há nada liberado a destacar.
 *
 * Misturar os dois (linhas em pendente, rodapé em liberado) foi o defeito
 * original: os nomes somavam 454k e o rodapé dizia 384k.
 */
export function rankPayouts<T extends PayoutRow>(
  technicians: readonly T[],
  opts: { urgent: boolean; top?: number },
): RankedPayouts<T> {
  const top = opts.top ?? 5;
  const metricOf = (r: PayoutRow) => (opts.urgent ? r.releasedTotal : r.pendingTotal);

  // Quem não tem nada a receber sob a métrica atual não ocupa linha.
  const ranked = technicians
    .filter((r) => metricOf(r) > 0)
    .sort((a, b) => metricOf(b) - metricOf(a));

  const rows = ranked.slice(0, top);
  const others = ranked.slice(top);
  return {
    rows,
    othersCount: others.length,
    othersTotal: others.reduce((s, r) => s + metricOf(r), 0),
    shownTotal: ranked.reduce((s, r) => s + metricOf(r), 0),
    metricOf,
  };
}
